import React from 'react';
import styled, { css } from 'styled-components';
import { Sparkles, HandHeart, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 140px 0;
  background: #FFFDF7;
  position: relative;
  overflow: hidden;
  @media (min-width: 768px) { padding: 180px 0; }
  &::before {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(26,18,8,0.08);
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 80px;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const Label = styled.p`
  font-family: 'Raleway', serif;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  color: #C07020;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  font-weight: 300;
  color: #1A1208;
  line-height: 1.0;
  letter-spacing: -0.01em;
  font-style: italic;
  max-width: 560px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 0 48px;
  padding: 60px 0;
  border-top: 1px solid rgba(26,18,8,0.08);
  transition: opacity 0.9s ease, transform 0.9s ease;
  transition-delay: ${({ $delay }) => $delay}ms;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(24px);`}
  @media (min-width: 768px) {
    grid-template-columns: 80px 1fr 1fr;
    align-items: center;
  }
`;

const Num = styled.span`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.06em;
  color: rgba(26,18,8,0.32);
  padding-top: 6px;
`;

const ItemTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 400;
  color: #1A1208;
  letter-spacing: -0.01em;
  margin-bottom: 12px;
  @media (min-width: 768px) { margin-bottom: 0; }
`;

const ItemDesc = styled.p`
  font-size: 0.9rem;
  font-weight: 300;
  color: rgba(26,18,8,0.58);
  line-height: 1.75;
  max-width: 380px;
  @media (min-width: 768px) { padding-left: 32px; border-left: 1px solid rgba(26,18,8,0.1); }
`;

const cards = [
  {
    Icon: Sparkles,
    en: { title: 'Quality Sourcing',          desc: 'Fresh whole milk sourced from trusted local farms. No powder, no shortcuts — just pure dairy from Jalisco.' },
    es: { title: 'Origen de Calidad',          desc: 'Leche entera fresca de granjas locales de confianza. Sin polvo, sin atajos — solo lácteo puro de Jalisco.' },
  },
  {
    Icon: HandHeart,
    en: { title: 'Artisan Process',            desc: 'Slow-cooked in traditional copper pots over an open flame, stirred by hand the same way it has been done since 1950.' },
    es: { title: 'Proceso Artesanal',          desc: 'Cocción lenta en ollas de cobre tradicionales sobre llama abierta, mezclada a mano como se ha hecho desde 1950.' },
  },
  {
    Icon: Globe,
    en: { title: 'International Standards',   desc: 'Fully compliant with COFEPRIS, SENASICA, and FDA regulations. D-U-N-S registered and export-ready to the U.S. market.' },
    es: { title: 'Estándares Internacionales', desc: 'Cumple con COFEPRIS, SENASICA y FDA. Registro D-U-N-S y listo para exportación al mercado estadounidense.' },
  },
];

export function Quality() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <Section id="calidad">
      <Container>
        <Top>
          <Label>{t('Quality & Craft', 'Calidad y Artesanía')}</Label>
          <Title>{t('Made the right\nway, always.', 'Hecho como\ndebe ser, siempre.')}</Title>
        </Top>
        <List ref={ref}>
          {cards.map(({ en, es }, i) => (
            <Row key={i} $inView={isInView} $delay={i * 120}>
              <Num>0{i + 1}</Num>
              <ItemTitle>{t(en.title, es.title)}</ItemTitle>
              <ItemDesc>{t(en.desc, es.desc)}</ItemDesc>
            </Row>
          ))}
        </List>
      </Container>
    </Section>
  );
}
